
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSlideHovered, setIsSlideHovered] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  
  const galleryImages = [
    '/images/1.jpg',
    '/images/2.jpg',
    '/images/3.jpg',
    '/images/4.jpg',
    '/images/5.jpg',
    '/images/6.jpg',
    '/images/7.jpg',
    '/images/8.jpg',
    '/images/9.jpg',
    '/images/10.jpg',
    '/images/11.jpg',
    '/images/12.jpg'
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const openFullscreen = () => {
    setIsFullscreenOpen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreenOpen(false);
  };

  useEffect(() => {
    // Add SEO meta tags dynamically
    document.title = 'Central Demolition & Services - Professional Demolition Services in New England';
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Central Demolition & Services",
      "description": "Professional demolition services for residential, commercial, and laboratory projects across New England",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "telephone": "857-777-4300",
      "email": "contact@central-demolition.com",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "New England",
        "addressCountry": "US"
      },
      "areaServed": [
        "Massachusetts",
        "Rhode Island", 
        "Connecticut",
        "New Hampshire",
        "Vermont",
        "Maine"
      ],
      "serviceType": [
        "Demolition Services",
        "Residential Demolition",
        "Commercial Demolition", 
        "Laboratory Demolition",
        "General Labor"
      ],
      "priceRange": "$$",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Demolition Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Residential Demolition",
              "description": "Complete home demolition services"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Commercial Demolition",
              "description": "Commercial and industrial demolition"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Laboratory Services",
              "description": "Specialized laboratory equipment demolition"
            }
          }
        ]
      }
    });
    document.head.appendChild(schemaScript);

    // Add fade-in animation class to elements on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-on-scroll');
    elements.forEach((el) => observer.observe(el));

    // Slideshow auto-advance
    const slideInterval = setInterval(() => {
      if (!isSlideHovered) {
        setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
      }
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(slideInterval);
      // Cleanup schema script
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [isSlideHovered, galleryImages.length]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/12d04496747239460c1d3b994d1545d8.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="w-full">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight px-2">
              Demolition with <span className="text-blue-400">Integrity</span>.<br />
              Services You Can <span className="text-blue-400">Trust</span>.
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed px-4">
              Serving Commercial, Residential & Laboratory Sites Across Massachusetts and All New England — 
              With Respect, Precision & Qualified Labor.
            </p>
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:space-x-4 justify-center items-center px-4">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button size="lg" className="text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-10 py-4 sm:py-5 w-full sm:w-auto whitespace-nowrap">
                  Contact us for free consultation
                </Button>
              </Link>
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm sm:text-base md:text-lg">
                <a href="tel:857-777-4300" className="hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill mr-2"></i>
                  857-777-4300
                </a>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <a href="mailto:contact@central-demolition.com" className="hover:text-blue-400 transition-colors cursor-pointer break-all sm:break-normal">
                  contact@central-demolition.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Central Demolition?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine decades of experience with unwavering commitment to safety, 
              professionalism, and respect for communities and cultural heritage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Fully Licensed &amp; Insured</h3>
              <p className="text-gray-600">
                Complete licensing and comprehensive insurance coverage for your peace of mind on every project.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Decades of Experience</h3>
              <p className="text-gray-600">
                Our skilled team brings combined decades of expertise in safe, controlled demolition practices.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Committed to Respect</h3>
              <p className="text-gray-600">
                Deep respect for safety protocols, community values, and cultural heritage in every project we undertake.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Professional Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From residential projects to complex laboratory demolitions, we deliver precise, 
              safe, and efficient services across all New England.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="relative overflow-hidden rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <img-comparison-slider hover="true" class="w-full h-64 object-cover object-top">
                <img slot="first" src="/images/residentialbefore.jpg" alt="Before Residential Demolition" />
                <img slot="second" src="/images/residentialafter.png" alt="After Residential Demolition" />
              </img-comparison-slider>
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold mb-2">Residential Demolition</h3>
                <p className="text-gray-600">Safe and efficient home demolition services with respect for neighborhoods.</p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <img-comparison-slider hover="true" class="w-full h-64 object-cover object-top">
                <img slot="first" src="/images/comercialbefore.png" alt="Before Commercial Demolition" />
                <img slot="second" src="/images/comercialafter.png" alt="After Commercial Demolition" />
              </img-comparison-slider>
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold mb-2">Commercial Demolition</h3>
                <p className="text-gray-600">Commercial and industrial demolition with precision and safety.</p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <img-comparison-slider hover="true" class="w-full h-64 object-cover object-top">
                <img slot="first" src="/images/laborbefore.jpg" alt="Before Laboratory Services" />
                <img slot="second" src="/images/laborafter.png" alt="After Laboratory Services" />
              </img-comparison-slider>
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold mb-2">Laboratory Services</h3>
                <p className="text-gray-600">Specialized laboratory and sensitive equipment demolition services.</p>
              </div>
            </div>
          </div>

          <div className="text-center fade-in-on-scroll">
            <Link to="/services">
              <Button size="lg">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
      {/* Gallery Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Work Done
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real images of services provided by Central Demolition. 
              See the quality and professionalism in each project.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto fade-in-on-scroll">
            <div 
              className="relative h-96 md:h-[500px] overflow-hidden rounded-lg shadow-2xl"
              onMouseEnter={() => setIsSlideHovered(true)}
              onMouseLeave={() => setIsSlideHovered(false)}
            >
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Trabalho realizado ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={openFullscreen}
                  />
                </div>
              ))}
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all duration-300 z-10"
                aria-label="Imagem anterior"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all duration-300 z-10"
                aria-label="Próxima imagem"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>

              {/* Slide Indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-white' 
                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                    }`}
                    aria-label={`Ir para imagem ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Image Modal */}
      {isFullscreenOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <div className="relative max-w-full max-h-full p-4">
            <img
              src={galleryImages[currentSlide]}
              alt={`Trabalho realizado ${currentSlide + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={closeFullscreen}
              className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all duration-300"
              aria-label="Fechar imagem"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Get a free consultation today. We're here to help you plan and execute 
              your demolition project safely and efficiently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="text-xl px-10 py-5">
                  Get Free Consultation
                </Button>
              </Link>
              <a href="tel:857-777-4300" className="text-2xl hover:text-blue-400 transition-colors cursor-pointer">
                <i className="ri-phone-fill mr-2"></i>
                857-777-4300
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Removido o script Neurolead duplicado - já está no index.html */}
    </div>
  );
}
